import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { useSelector } from 'react-redux';

import { ArrowDown, CalendarNumberSale, CheckMark, Clock } from '@/icons/index';
import { generateTimeSlots } from '@/utils/generateTimeSlots';
import useWorkDays from '@/hooks/useGetWorkDays';

import styles from './index.module.scss';

const SelectDateTime = ({ formik }) => {
  const { t } = useTranslation();

  const [selectedDate, setSelectedDate] = useState('');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const dateDropdownRef = useRef(null);

  const [selectedHour, setSelectedHour] = useState('');
  const [isHourDropdownOpen, setIsHourDropdownOpen] = useState(false);
  const hourDropdownRef = useRef(null);
  const isDateDropdownDisabled =
    !formik.values.pickup_point_id &&
    !formik.values.address_coordinates.address;

  const { selectedBranch: selectedBranchId } = useSelector(
    (state) => state.miniMarket,
  );
  const optionsDate = useWorkDays();

  const optionsHours = generateTimeSlots('10:00', '18:00', 15);

  useEffect(() => {
    setSelectedDate(
      formik.values.dateType || t('numbersale:checkout.selectDate'),
    );
    setSelectedHour(
      formik.values.hourType || t('numbersale:checkout.selectHour'),
    );
  }, [formik.values.dateType, formik.values.hourType, t]);

  const handleDateOptionClick = (option) => {
    setSelectedDate(option);
    formik.setFieldValue('dateType', option);
    setIsDateDropdownOpen(false);
  };

  const handleHourOptionClick = (option) => {
    setSelectedHour(option);
    formik.setFieldValue('hourType', option);
    setIsHourDropdownOpen(false);
  };

  const handleDateSelectClick = () => {
    if (!isDateDropdownDisabled) {
      setIsDateDropdownOpen((prev) => !prev);
    }
  };

  const handleHourSelectClick = () => {
    if (selectedDate !== t('numbersale:checkout.selectDate')) {
      setIsHourDropdownOpen((prev) => !prev);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(event.target)
      ) {
        setIsDateDropdownOpen(false);
      }
      if (
        hourDropdownRef.current &&
        !hourDropdownRef.current.contains(event.target)
      ) {
        setIsHourDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    formik.setFieldValue('dateType', '');
    formik.setFieldValue('hourType', '');
  }, [selectedBranchId]);

  const renderOption = (option, index) => {
    const [date, day] = option.split(', ');
    return (
      <div
        key={index}
        onClick={() => handleDateOptionClick(option)}
        itemScope
        itemType='https://schema.org/Property'
      >
        <span className={styles.weekDays}>
          {date},&nbsp;<span>{day}</span>
        </span>
      </div>
    );
  };

  return (
    <div
      className={styles.selectDateTimeContainer}
      itemScope
      itemType='https://schema.org/Date'
    >
      <div className={styles.custom_select} ref={dateDropdownRef}>
        <div
          className={classNames(styles.dropdown_selected, {
            [styles.select_arrow_active]: isDateDropdownOpen,
            [styles.disabled]: isDateDropdownDisabled,
          })}
          onClick={handleDateSelectClick}
          data-testid={'select-date'}
        >
          <div className={styles.innerContainer}>
            <span>
              {!formik.values.dateType ? (
                <CalendarNumberSale />
              ) : (
                <CheckMark className={styles.checkIcon} />
              )}
            </span>
            {selectedDate}
          </div>
          <ArrowDown
            className={classNames({
              [styles.arrow_right_date]: isDateDropdownOpen,
            })}
          />
        </div>
        {isDateDropdownOpen && (
          <div className={styles.select_items}>
            {optionsDate.map(renderOption)}
          </div>
        )}
      </div>
      <div
        className={classNames(styles.custom_select, {
          [styles.disabled]: !formik.values.dateType,
        })}
        ref={hourDropdownRef}
      >
        <div
          className={classNames(styles.dropdown_selected, {
            [styles.select_arrow_active]: isHourDropdownOpen,
          })}
          onClick={handleHourSelectClick}
          data-testid={'select-hour'}
        >
          <div className={styles.innerContainer}>
            <span>
              {selectedHour === t('numbersale:checkout.selectHour') ? (
                <Clock />
              ) : (
                <CheckMark className={styles.checkIcon} />
              )}
            </span>
            {selectedHour}
          </div>
          <ArrowDown
            className={classNames({
              [styles.arrow_right_hour]: isHourDropdownOpen,
            })}
          />
        </div>
        {isHourDropdownOpen && (
          <div className={styles.select_items}>
            {optionsHours.map((option, index) => (
              <div key={index} onClick={() => handleHourOptionClick(option)}>
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
export default SelectDateTime;
